import { isInRovoFullscreenPages } from '@townsquare/chat-view/__router-route';
import { useRouter } from '@townsquare/router/useRouter';
import { sharedConversationPreviewRoute } from '@townsquare/routes/shared-conversation-preview';
import { useRovoAppification } from '@townsquare/rovo/appification';
import { useRovoEntitlement } from '@townsquare/rovo/entitlement';

import { useIsOnFullscreenSearch, useIsOnRovoSearch } from './hooks';

/**
 * The navigation storage of the side nav `ConditionalSideNav` renders for the current route, so the
 * shared GlobalRoot top nav reads and writes the same store. Only the default Home side nav uses
 * network settings; the Rovo and search side navs use local storage.
 *
 * Only used by the gated `navx-global-top-nav-home` route. The branching mirrors
 * `ConditionalSideNav` and must be kept in step with it.
 */
export function useHomeNavigationCustomizationStore(
	customizationDisabled: boolean | undefined,
): 'local' | 'network-migration' {
	const [routerState] = useRouter();
	const isOnRovoSearchRoute = useIsOnRovoSearch();
	const isOnFullscreenSearchRoute = useIsOnFullscreenSearch();
	const { data: rovoEntitlement } = useRovoEntitlement();
	const { rovoAppificationState } = useRovoAppification();

	const homeStore = customizationDisabled ? 'local' : 'network-migration';

	if (rovoAppificationState === 'enabled') {
		return 'local';
	}

	if (rovoAppificationState === 'disabled') {
		const inRovoFullScreenPages = isInRovoFullscreenPages(routerState.route.name);
		const isSharedPreview = routerState.route.name === sharedConversationPreviewRoute.name;
		if (inRovoFullScreenPages && !isSharedPreview && rovoEntitlement?.isRovoLLMEnabled) {
			return 'local';
		}

		if (isOnRovoSearchRoute || isOnFullscreenSearchRoute) {
			return 'local';
		}
	}

	return homeStore;
}
